// user.ts
import { userRoot } from "../constants/apiRoutes";
import { IUserRootResponse } from "../types/user";
import { crudUtil } from "./axiosCRUD";
import Cookies from "js-cookie";
import { getDefaultStore } from "jotai";
import { jwtDecode } from "jwt-decode";
import {
  userDataAtom,
  userLoadingAtom,
  userErrorAtom,
} from "../store/userStore";
const envCookieString = "";
/**
 * Get user root folder data with cookies and store in Jotai
 * @param signal - Optional abort signal for request cancellation
 * @param useStore - Whether to update the Jotai store (default: true)
 * @returns Promise<IUserRootResponse>
 */
export async function getUser(
  signal?: AbortSignal,
  useStore: boolean = false
): Promise<IUserRootResponse> {
  // CRITICAL: User API must call external app.dadan.io, NOT local proxy
  // All other APIs (/api/projects, /api/AI, etc.) use local proxy
  const endpoint = `https://app.dadan.io${userRoot}`;
  const store = getDefaultStore();
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    process.env.REACT_ENV === "local";

  if (useStore) {
    store.set(userLoadingAtom, true);
    store.set(userErrorAtom, null);
  }

  try {
    const setCookiesFromEnv = () => {
      if (envCookieString?.length) {
        const cookies = envCookieString?.split(";");
        cookies?.forEach((cookie: any) => {
          const trimmedCookie = cookie.trim();
          const equalIndex = trimmedCookie.indexOf("=");
          if (equalIndex > 0) {
            const name = trimmedCookie.substring(0, equalIndex);
            const value = trimmedCookie.substring(equalIndex + 1);
            if (name && value) {
              // Fixed cookie settings
              Cookies.set(name, value, {
                domain: isLocalhost ? "localhost" : ".dadan.io",
                secure: !isLocalhost, // false for localhost
                sameSite: isLocalhost ? "lax" : "none",
                path: "/",
              });
            }
          }
        });
      }
    };

    setCookiesFromEnv();

    const response = await crudUtil.get<IUserRootResponse>(
      endpoint,
      {},
      {
        signal,
        withCredentials: process.env.REACT_ENV === "local", // This will automatically send cookies
        headers: {
          accept: "application/json, text/plain, */*",
          "accept-language": "en-US,en;q=0.9",
          // Remove manual Cookie header - let browser handle it
        },
      }
    );

    if (useStore) {
      store.set(userDataAtom, response);
      store.set(userLoadingAtom, false);
      store.set(userErrorAtom, null);
    }

    return response;
  } catch (error) {
    if (useStore) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      store.set(userErrorAtom, errorMessage);
      store.set(userLoadingAtom, false);
    }
    throw error;
  }
}

/**
 * Extract email from JWT token payload
 * @param payload - JWT token payload
 * @returns email string or null if not found
 */
const extractEmailFromToken = (payload: any): string | null => {
  // Try different JWT claim formats for email
  const email =
    payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"] ||
    payload[
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
    ] ||
    payload.email ||
    payload.emailaddress ||
    payload.name ||
    payload.sub ||
    payload.preferred_username;

  return email || null;
};

/**
 * Check if user is authenticated based on SPAExtensionSSO cookie
 * Also validates JWT token expiration and email match with current userData
 * @returns boolean indicating if user is authenticated
 */
export const isUserAuthenticated = (): boolean => {
  const ssoToken = Cookies.get("SPAExtensionSSO");
  if (!ssoToken) {
    return false;
  }

  // Get current user data from store
  const store = getDefaultStore();
  const userData = store.get(userDataAtom);
  try {
    const decodedToken = jwtDecode(ssoToken);
    // Try to decode JWT token to check expiration and email

    // Check if token has expired
    if (decodedToken.exp && decodedToken.exp < Date.now() / 1000) {
      // Token has expired, remove it
      Cookies.remove("SPAExtensionSSO");
      return false;
    }
    // const tokenEmail = extractEmailFromToken(payload);

    // Check if userData exists and email matches
    // if (
    //   userData &&
    //   userData.email &&
    //   tokenEmail &&
    //   tokenEmail === userData.email
    // ) {
    //   return true;
    // }

    return true;
  } catch (error) {
    // If we can't decode the token, assume it's invalid
    console.warn(
      "Authentication check: Failed to decode SPAExtensionSSO token:",
      error
    );
    return false;
  }
};

/**
 * Get the current user's email from the SPAExtensionSSO token
 * @returns email string or null if not found
 */
export const getCurrentUserEmailFromToken = (): string | null => {
  const ssoToken = Cookies.get("SPAExtensionSSO");

  if (!ssoToken) {
    return null;
  }

  try {
    const parts = ssoToken.split(".");
    if (parts.length === 3) {
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
      );
      return extractEmailFromToken(payload);
    }
  } catch (error) {
    console.warn("Failed to extract email from token:", error);
  }

  return null;
};

/**
 * Helper function to get specific cookies using js-cookie
 * @param cookieName - Name of the cookie to retrieve
 * @returns Cookie value or undefined
 */
export function getCookie(cookieName: string): string | undefined {
  return Cookies.get(cookieName);
}

/**
 * Helper function to set a cookie using js-cookie
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Cookie options
 */
export function setCookie(
  name: string,
  value: string,
  options?: Cookies.CookieAttributes
): void {
  Cookies.set(name, value, options);
}

/**
 * Helper function to remove a cookie using js-cookie
 * @param name - Cookie name
 * @param options - Cookie options
 */
export function removeCookie(
  name: string,
  options?: Cookies.CookieAttributes
): void {
  Cookies.remove(name, options);
}
